import "react-checkbox-tree/lib/react-checkbox-tree.css";

import {
    SaveWrapper,
    SavedContainer,
    SelectWrapper,
    Subtitle,
} from "../Filter.styles";
import { useCallback, useEffect, useRef, useState } from "react";

import CustomFilterModal from "features/allocations/components/modals/CustomFilter";
import Delete from "shared/components/utils/icons/Delete";
import Disk from "shared/components/utils/icons/Disk";
import Icon from "shared/components/utils/Icon";
import IconButton from "shared/components/inputs/button/IconButton";
import { MenuItem as MoreMenuItem } from "@szhsin/react-menu";
import Objects from "shared/helper-functions/objects/objectHelpers";
import PropTypes from "prop-types";
import SelectMenu from "shared/components/inputs/menu/SelectMenu";
import Text from "shared/components/data-display/mui/Text";
import Transform from "features/allocations/api/transform";
import { useCustomFilters } from "features/allocations/api/useCustomFilters";
import { useStylesForMenu } from "shared/styles/makeStyles";

const SavedFilters = ({
    selectedFilters,
    onChange,
    onClear,
    selectedCustomFilter,
    onFilterCreated,
}) => {
    const customFilterRef = useRef();
    const classes = useStylesForMenu();
    const [savedFilters, setSavedFilters] = useState([]);
    const { getCustomFilter } = useCustomFilters();

    const [reloadSavedFilters, setReloadSavedFilters] = useState(false);

    const changeClientFilterToArray = useCallback((data) => {
        return data.map((filter) => {
            const newData = { ...filter.data };
            
            if (filter.data?.clientFamily) {
                newData.clientFamily = Array.isArray(filter.data.clientFamily)
                    ? filter.data.clientFamily
                    : [filter.data.clientFamily];
            }
            
            if (filter.data?.client) {
                newData.client = Array.isArray(filter.data.client)
                    ? filter.data.client
                    : [filter.data.client];
            }
            
            return {
                ...filter,
                data: newData,
            };
        });
    }, []);

    const getSavedFilters = useCallback(async (filterId = null) => {
        const payload = await getCustomFilter();
        const trasnformedData = Transform.customFilters(payload, filterId);
        const data = changeClientFilterToArray(trasnformedData);
        return data;
    }, [getCustomFilter, changeClientFilterToArray]);

    const initFilters = useCallback(async (filterId) => {
        let shouldFetch = false;

        setSavedFilters((prevFilters) => {
            if (prevFilters && prevFilters.length > 0) {
                // If there are already filters, only update the default
                return prevFilters.map((item) => {
                    return {
                        ...item,
                        default: item.id === filterId,
                    };
                });
            } else {
                // If there are no filters, mark to fetch later
                shouldFetch = true;
                return prevFilters;
            }
        });

        // Fetch outside the setState
        if (shouldFetch) {
            const data = await getSavedFilters(filterId);
            setSavedFilters(data);
        }
    }, [getSavedFilters]);

    useEffect(() => {
        initFilters(selectedCustomFilter);
    }, [selectedCustomFilter, initFilters]);

    const handleCustomFilterCreated = (filter) => {
        const customFilter = {
            id: filter.Id,
            name: filter.Name,
            data: Objects.parseJson(filter.Value),
            default: true,
        };

        setSavedFilters((prevFilters) => [
            ...prevFilters.map((item) => ({ ...item, default: false })),
            customFilter,
        ]);

        onFilterCreated(customFilter.id);
    };

    const handleCustomFilterUpdated = (filter) => {
        setSavedFilters((prevFilters) =>
            prevFilters.map((item) =>

                item.id === filter.id
                    ? { ...item, name: filter.name }
                    : item
            )
        );
    };

    const handleCustomFilterDeleted = (filterId) => {
        setSavedFilters((prevFilters) =>
            prevFilters.filter((item) => item.id !== filterId)
        );
    };

    const onClearSavedFilter = () => {
        setReloadSavedFilters(true);
        onClear();
    };

    const onChangeSavedFilter = (filter) => {
        onChange(filter);
    };

    const showSaveFilterSection = (event) => {
        customFilterRef.current.add(selectedFilters);
    };

    return (
        <>
            <Subtitle>
                <Text variant="xsmall">Saved Filters</Text>
            </Subtitle>
            <SavedContainer>
                <SelectWrapper lg={10}>
                    <SelectMenu
                        key={0}
                        label="Select Saved Filters"
                        items={savedFilters}
                        itemAction={(filter) => onChangeSavedFilter(filter)}
                        clearAction={() => onClearSavedFilter()}
                        reload={reloadSavedFilters}
                        border
                        triggerLength="100%"
                        moreMenuOptions={[
                            <MoreMenuItem
                                key={0}
                                onClick={(e) =>
                                    customFilterRef.current.rename(
                                        e.value.id,
                                        e.value.name
                                    )
                                }
                            >
                                <Icon name="edit" />
                                Rename
                            </MoreMenuItem>,
                            <MoreMenuItem
                                key={1}
                                onClick={(e) =>
                                    customFilterRef.current.delete(
                                        e.value.id,
                                        e.value.name
                                    )
                                }
                            >
                                <Delete size="16" />
                                Delete
                            </MoreMenuItem>,
                        ]}
                    />
                </SelectWrapper>
                <CustomFilterModal
                    ref={customFilterRef}
                    onCreate={(filter) => handleCustomFilterCreated(filter)}
                    onUpdate={(filter) => handleCustomFilterUpdated(filter)}
                    onDelete={(filterId) => handleCustomFilterDeleted(filterId)}
                />
                <SaveWrapper>
                    <IconButton
                        icon={<Disk />}
                        onClick={(event) => showSaveFilterSection(event)}
                        type="button"
                        className={classes.diskButton}
                    />
                </SaveWrapper>
            </SavedContainer>
        </>
    );
};
SavedFilters.propTypes = {
    selectedFilters: PropTypes.object,
    onChange: PropTypes.func,
    onClear: PropTypes.func,
    selectedCustomFilter: PropTypes.number,
    onFilterCreated: PropTypes.func,
};

export default SavedFilters;
